import React, { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Plus,
  Clock,
  Eye,
  AlertTriangle,
  FileText,
  RefreshCw,
} from "lucide-react";
import { useUser } from "@clerk/clerk-react";
import { apiClient } from "../config/api";
import API_CONFIG from "../config/api";
import "./incidents.scss";
import IncidentDetailView from "./Incidents/IncidentDetailView";
import CreateIncidentModal from "./Incidents/CreateIncidentModal";
import PageLoader from "../components/Common/PageLoader";
import { DataTable } from "../components/Common/DataTable";
import IncidentTimeline from "./Incidents/IncidentTimeline";
import { useClientFilter } from "../hooks/useClientFilter";

// Filter options for dropdowns
const SEVERITY_OPTIONS = ["Critical", "High", "Medium", "Low"];
const STATUS_OPTIONS = ["New", "Open", "In Progress", "Resolved", "Closed"];

// Helper function to format date/time
function formatDateTime(dateString) {
  if (!dateString) return "-";
  try {
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return dateString;
  }
}

// Helper function to parse techniques array
function parseTechniques(techniques) {
  if (!techniques) return "-";
  try {
    const parsed = JSON.parse(techniques);
    if (Array.isArray(parsed) && parsed.length > 0) {
      return parsed.join(", ");
    }
    return "-";
  } catch {
    return techniques;
  }
}

function getSeverityClass(severity) {
  switch (severity) {
    case "Critical":
      return "badge badge--critical";
    case "High":
      return "badge badge--high";
    case "Medium":
      return "badge badge--medium";
    case "Low":
      return "badge badge--low";
    default:
      return "badge";
  }
}

function getStatusClass(status) {
  switch (status) {
    case "Open":
      return "chip chip--danger";
    case "In Progress":
      return "chip chip--warn";
    case "Resolved":
      return "chip chip--success";
    case "Closed":
      return "chip";
    default:
      return "chip";
  }
}

export default function Incidents() {
  const [tab, setTab] = useState("all");
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [apiResponse, setApiResponse] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const detailRequestKeyRef = useRef(null);
  const loadedIncidentIdRef = useRef(null);

  // Get incident ID from URL params
  const { incidentId } = useParams();
  const navigate = useNavigate();

  // Get user info from Clerk
  const { user } = useUser();
  const userClient = user?.publicMetadata?.client;
  const isHelxon = userClient?.name === "Helxon";

  // Use client filter hook for filtering incidents by selected client
  const { filterByClient, selectedClientId, selectedClient } = useClientFilter();

  // API call function
  const fetchIncidents = async () => {
    try {
      setLoading(true);
      setError(null);

      const apiUrl = API_CONFIG.ENDPOINTS.INCIDENTS;
      console.log("Making API call to", apiUrl);

      const response = await apiClient.get(apiUrl, {
        headers: {
          Accept: "text/plain",
        },
      });

      setApiResponse(response.data);

      // Transform API data to match our component structure
      if (Array.isArray(response.data)) {
        const transformedIncidents = response.data.map((incident, index) => ({
          // Visible columns by default
          id: incident.incidentNumber || index + 1,
          title: incident.title || "API Incident",
          severity: incident.severity || "Medium",
          status: incident.status || "Open",
          created: incident.createdAt || new Date().toISOString(),

          // Hidden columns by default (additional data)
          description: incident.description || "-",
          lastActivityTime: incident.lastActivityTime || null,
          lastModifiedTime: incident.lastModifiedTime || null,
          alertsCount: incident.alertsCount || 0,
          additionalData: incident.additionalData || null,
          firstActivityTime: incident.firstActivityTime || null,
          providerName: incident.providerName || "-",
          sourceSystem: incident.sourceSystem || "-",
          techniques: incident.techniques || "[]",

          // Client UUID for filtering
          clientUUID: incident.clientUUID || null,

          // Assignee fields
          helxonAssignedTo: incident.helxonAssignedTo || null,
          customerAssignedTo: incident.customerAssignedTo || null,

          // Keep original data for detail view
          _raw: incident,
        }));

        setIncidents(transformedIncidents);
      } else {
        console.log("API response is not an array");
        setIncidents([]);
      }
    } catch (err) {
      console.error("API Error:", err);
      console.error("Error Details:", {
        message: err.message,
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
      });

      setError(err.message);
      setIncidents([]);
    } finally {
      setLoading(false);
    }
  };

  // Load incidents on component mount
  useEffect(() => {
    fetchIncidents();
  }, []);

  const loadIncidentDetails = useCallback(
    async (incident) => {
      if (!incident) return;

      const source = incident._raw || incident;
      const incidentName =
        source.incidentName ||
        source.id ||
        source.incidentId ||
        source.incidentUUID ||
        source.incidentNumber ||
        incident.id;

      if (!incidentName) {
        setDetailError("Missing incident identifier.");
        setSelected(incident);
        return;
      }

      const requestKey = String(incidentName);
      if (detailRequestKeyRef.current === requestKey) {
        // Already loading or loaded this incident; skip duplicate fetch
        setSelected((prev) => prev || incident);
        return;
      }
      detailRequestKeyRef.current = requestKey;

      setDetailLoading(true);
      setDetailError(null);
      setSelected(incident);

      try {
        const { data } = await apiClient.get(
          API_CONFIG.ENDPOINTS.INCIDENT_DETAILS(incidentName),
          { headers: { Accept: "application/json" } }
        );

        const detailedIncident = data?.incident || data || {};

        // Try every location the backend may place the alerts array
        const alerts =
          data?.alerts ||
          detailedIncident?.alerts ||
          data?.incident?.alerts ||
          data?.data?.alerts ||
          detailedIncident?._raw?.alerts ||
          [];

        const attachments = data?.attachments || detailedIncident?.attachments || [];

        const merged = {
          ...incident,
          ...detailedIncident,
          id:
            detailedIncident.incidentNumber ??
            detailedIncident.id ??
            incident.id,
          title: detailedIncident.title || incident.title,
          severity: detailedIncident.severity || incident.severity,
          status: detailedIncident.status || incident.status,
          description:
            detailedIncident.description ?? incident.description ?? "",
          // Include new assignee fields from API response
          helxonAssignedTo: data?.helxonAssignedTo || null,
          customerAssignedTo: data?.customerAssignedTo || null,
          clientName: data?.clientName || detailedIncident.clientName || null,
          _raw: { ...(incident._raw || {}), ...(detailedIncident || {}) },
          alerts,
          attachments,
        };

        setSelected(merged);
      } catch (err) {
        console.error("Failed to load incident details", err);
        setDetailError(err.response?.data?.message || err.message);
      } finally {
        setDetailLoading(false);
        detailRequestKeyRef.current = null;
      }
    },
    []
  );

  // Open incident from URL parameter when incidents are loaded
  // NOTE: `selected` is intentionally NOT in the dependency array to avoid a
  // feedback loop (selected changes → effect re-runs → loads again → selected changes …).
  // Click-driven opens are handled by handleSelectIncident which sets
  // loadedIncidentIdRef so this effect skips the duplicate call.
  useEffect(() => {
    if (incidentId && incidents.length > 0) {
      // Skip if this incident was already loaded / is currently loading (click-driven)
      if (loadedIncidentIdRef.current === String(incidentId)) return;

      const incidentFromUrl = incidents.find(
        (inc) => String(inc.id) === String(incidentId)
      );
      if (incidentFromUrl) {
        loadedIncidentIdRef.current = String(incidentId);
        loadIncidentDetails(incidentFromUrl);
      }
    } else if (!incidentId && loadedIncidentIdRef.current !== null) {
      // URL no longer has incident ID – close the modal (only if not already cleared)
      loadedIncidentIdRef.current = null;
      setSelected(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incidentId, incidents, loadIncidentDetails]);

  // Handle selecting an incident (updates URL)
  const handleSelectIncident = useCallback(
    (incident) => {
      if (incident) {
        // Mark this incident as handled so the URL-watching effect skips the duplicate call
        loadedIncidentIdRef.current = String(incident.id);
        navigate(`/incidents/${incident.id}`, { replace: true });
        loadIncidentDetails(incident);
      }
    },
    [loadIncidentDetails, navigate]
  );

  // Handle closing incident modal (clears URL)
  const handleCloseIncident = useCallback(() => {
    loadedIncidentIdRef.current = null;
    detailRequestKeyRef.current = null;
    setSelected(null);
    setDetailError(null);
    setDetailLoading(false);
    navigate('/incidents', { replace: true });
  }, [navigate]);

  // Define table columns
  const columns = useMemo(
    () => [
      // ===== VISIBLE BY DEFAULT =====
      {
        accessorKey: "id",
        header: "ID",
        size: 70,
        cell: ({ getValue }) => (
          <span className="chip chip--muted font-mono">{getValue()}</span>
        ),
      },
      {
        accessorKey: "title",
        header: "Title",
        size: 280,
        cell: ({ getValue }) => (
          <span className="incident__title">{getValue()}</span>
        ),
      },
      {
        accessorKey: "severity",
        header: "Severity",
        size: 100,
        filterOptions: SEVERITY_OPTIONS,
        cell: ({ getValue }) => {
          const severity = getValue();
          return <span className={getSeverityClass(severity)}>{severity}</span>;
        },
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 100,
        filterOptions: STATUS_OPTIONS,
        cell: ({ getValue }) => {
          const status = getValue();
          return <span className={getStatusClass(status)}>{status}</span>;
        },
      },
      {
        accessorKey: "helxonAssignedTo",
        header: "Helxon Assigned To",
        size: 180,
        cell: ({ getValue }) => {
          const assignee = getValue();
          if (!assignee) return <span className="text-muted">—</span>;
          const displayName = assignee.displayName || assignee.userPrincipalName || assignee.email || "—";
          return (
            <span  title={assignee.email || ""}>
              {displayName}
            </span>
          );
        },
      },
      {
        accessorKey: "customerAssignedTo",
        header: "Customer Assigned To",
        size: 180,
        cell: ({ getValue }) => {
          const assignee = getValue();
          if (!assignee) return <span className="text-muted">—</span>;
          const displayName = assignee.displayName || assignee.userPrincipalName || assignee.email || "—";
          return (
            <span title={assignee.email || ""}>
              {displayName}
            </span>
          );
        },
      },
      {
        accessorKey: "created",
        header: "Created",
        size: 160,
        cell: ({ getValue }) => (
          <div className="inline">
            <Clock size={12} className="icon-muted" />
            <span className="font-mono small">
              {formatDateTime(getValue())}
            </span>
          </div>
        ),
      },

      // ===== HIDDEN BY DEFAULT (Additional Columns) =====
      {
        accessorKey: "description",
        header: "Description",
        size: 350,
        cell: ({ getValue }) => {
          const desc = getValue();
          const truncated =
            desc && desc.length > 100 ? desc.substring(0, 100) + "..." : desc;
          return (
            <span className="text-muted" title={desc}>
              {truncated || "-"}
            </span>
          );
        },
      },
      {
        accessorKey: "lastActivityTime",
        header: "Last Activity",
        size: 160,
        cell: ({ getValue }) => (
          <div className="inline">
            <Clock size={12} className="icon-muted" />
            <span className="font-mono small">
              {formatDateTime(getValue())}
            </span>
          </div>
        ),
      },
      {
        accessorKey: "lastModifiedTime",
        header: "Last Modified",
        size: 160,
        cell: ({ getValue }) => (
          <div className="inline">
            <Clock size={12} className="icon-muted" />
            <span className="font-mono small">
              {formatDateTime(getValue())}
            </span>
          </div>
        ),
      },
      {
        accessorKey: "alertsCount",
        header: "Alerts Count",
        size: 110,
        cell: ({ getValue }) => {
          const count = getValue();
          return (
            <div className="inline">
              <AlertTriangle size={12} className="icon-muted" />
              <span
                className={
                  count > 0 ? "badge badge--medium" : "chip chip--muted"
                }
              >
                {count}
              </span>
            </div>
          );
        },
      },
      {
        accessorKey: "additionalData",
        header: "Additional Data",
        size: 200,
        enableSorting: false,
        cell: ({ getValue }) => {
          const data = getValue();
          if (!data) return <span className="text-muted">-</span>;
          try {
            const parsed = JSON.parse(data);
            return (
              <span className="chip chip--muted font-mono small" title={data}>
                {parsed.tactics?.join(", ") || "View Data"}
              </span>
            );
          } catch {
            return <span className="text-muted">-</span>;
          }
        },
      },
      {
        accessorKey: "firstActivityTime",
        header: "First Activity",
        size: 160,
        cell: ({ getValue }) => (
          <div className="inline">
            <Clock size={12} className="icon-muted" />
            <span className="font-mono small">
              {formatDateTime(getValue())}
            </span>
          </div>
        ),
      },
      {
        accessorKey: "providerName",
        header: "Provider Name",
        size: 180,
        cell: ({ getValue }) => (
          <span className="chip chip--muted">{getValue()}</span>
        ),
      },
      {
        accessorKey: "sourceSystem",
        header: "Source System",
        size: 140,
        cell: ({ getValue }) => (
          <span className="chip chip--muted">{getValue()}</span>
        ),
      },
      {
        accessorKey: "techniques",
        header: "Techniques",
        size: 140,
        cell: ({ getValue }) => {
          const techniques = parseTechniques(getValue());
          return (
            <span
              className={techniques !== "-" ? "badge badge--low" : "text-muted"}
            >
              {techniques}
            </span>
          );
        },
      },
    ],
    [handleSelectIncident]
  );

  // Initial column visibility - hide additional columns by default
  const initialColumnVisibility = useMemo(
    () => ({
      description: false,
      lastActivityTime: false,
      lastModifiedTime: false,
      alertsCount: false,
      additionalData: false,
      firstActivityTime: false,
      providerName: false,
      sourceSystem: false,
      techniques: false,
    }),
    []
  );

  // Filter incidents by selected client first
  const clientFilteredIncidents = useMemo(() => {
    return filterByClient(incidents, 'clientUUID');
  }, [incidents, filterByClient]);

  // Calculate counts for tabs (based on client-filtered incidents)
  const counts = useMemo(
    () => ({
      all: clientFilteredIncidents.length,
      open: clientFilteredIncidents.filter((i) => i.status === "Open").length,
      inProgress: clientFilteredIncidents.filter((i) => i.status === "In Progress").length,
      critical: clientFilteredIncidents.filter((i) => i.severity === "Critical").length,
    }),
    [clientFilteredIncidents]
  );

  // Filter incidents based on tab (after client filtering)
  const filteredData = useMemo(() => {
    switch (tab) {
      case "open":
        return clientFilteredIncidents.filter((i) => i.status === "Open");
      case "inProgress":
        return clientFilteredIncidents.filter((i) => i.status === "In Progress");
      case "critical":
        return clientFilteredIncidents.filter((i) => i.severity === "Critical");
      default:
        return clientFilteredIncidents;
    }
  }, [tab, clientFilteredIncidents]);

  // Handle row click
  const handleRowClick = (incident) => {
    handleSelectIncident(incident);
  };

  if (loading) {
    return <PageLoader loading={loading} />;
  }

  return (
    <div className="incidents">
      {/* Header */}
      <div className="incidents__header">
        <div>
          <h2>Incident Management</h2>
          <p>Manage and track security incidents</p>
          {/* API Status Indicator */}
          <div className="api-status">
            {loading && (
              <span className="api-status__loading">
                🔄 Loading from API...
              </span>
            )}
            {error && (
              <span className="api-status__error">❌ API Error: {error}</span>
            )}
          </div>
        </div>
        <div className="incidents__actions">
          <button className="btn btn--primary" onClick={fetchIncidents}>
            <RefreshCw size={16} /> Refresh
          </button>
          {isHelxon && (
            <button
              className="btn btn--primary"
              onClick={() => setShowCreateModal(true)}
            >
              <Plus size={16} /> New Incident
            </button>
          )}
        </div>
      </div>

      {/* Activity Timeline & Action Needed */}
      <IncidentTimeline incidents={clientFilteredIncidents} />

      {/* DataTable */}
      <DataTable
        data={filteredData}
        columns={columns}
        onRowClick={handleRowClick}
        enableSearch={true}
        enableFilters={true}
        enableColumnVisibility={true}
        enableSorting={true}
        enablePagination={true}
        enableExport={true}
        enableDensity={true}
        pageSize={10}
        pageSizeOptions={[5, 10, 20, 50, 100]}
        emptyMessage="No incidents found"
        loading={loading}
        initialColumnVisibility={initialColumnVisibility}
      />

      {/* Incident Detail Modal */}
      {selected && (
        <IncidentDetailView
          incident={selected}
          loading={detailLoading}
          error={detailError}
          onClose={handleCloseIncident}
          onUpdate={(updatedIncident) => {
            // Merge updated data with existing incident
            setSelected((prev) => ({
              ...prev,
              ...updatedIncident,
              helxonAssignedTo: updatedIncident.helxonAssignedTo,
              customerAssignedTo: updatedIncident.customerAssignedTo,
              status: updatedIncident.status || prev.status,
              severity: updatedIncident.severity || prev.severity,
            }));
            // Refresh the incidents table to show updated data
            fetchIncidents();
          }}
        />
      )}

      {/* Create Incident Modal - Only for Helxon users */}
      {showCreateModal && isHelxon && (
        <CreateIncidentModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={(newIncident) => {
            // Refresh incidents list after successful creation
            fetchIncidents();
          }}
        />
      )}
    </div>
  );
}
